/**
 * Socket.io contract: typed event maps + zod payload schemas.
 *
 * Namespace: "/". Handshake auth: { token: sessionToken, roomId }.
 * All sync:* and track:* events are host-only (enforced server-side).
 * FROZEN after WS-0.
 */
import { z } from 'zod';
import { LIMITS } from './constants.js';
import { ErrorCode } from './errors.js';
import { MemberSchema } from './rooms.js';
import { PlaybackStateSchema, RoleSchema, RoomSummarySchema } from './rooms.js';
// ── Client -> Server payloads ────────────────────────────────────────────
export const RoomJoinPayloadSchema = z.object({ roomId: z.string().min(1) }).strict();
export const RoomLeavePayloadSchema = z.object({ roomId: z.string().min(1) }).strict();
export const SyncPlayPayloadSchema = z
    .object({ roomId: z.string().min(1), position: z.number().finite().nonnegative() })
    .strict();
export const SyncPausePayloadSchema = z
    .object({ roomId: z.string().min(1), position: z.number().finite().nonnegative() })
    .strict();
export const SyncSeekPayloadSchema = z
    .object({ roomId: z.string().min(1), position: z.number().finite().nonnegative() })
    .strict();
export const SyncRatePayloadSchema = z
    .object({
    roomId: z.string().min(1),
    // WHY: z.enum only accepts strings; use a union of z.literal for numeric rates.
    rate: z.union([
        z.literal(0.5),
        z.literal(0.75),
        z.literal(1),
        z.literal(1.25),
        z.literal(1.5),
        z.literal(2),
    ]),
})
    .strict();
export const TrackAudioPayloadSchema = z
    .object({ roomId: z.string().min(1), trackId: z.string().min(1) })
    .strict();
export const TrackSubtitlePayloadSchema = z
    .object({ roomId: z.string().min(1), trackId: z.string().min(1).nullable() })
    .strict();
export const RoomClaimHostPayloadSchema = z
    .object({ roomId: z.string().min(1), hostKey: z.string().min(1).max(64) })
    .strict();
export const RoomKickPayloadSchema = z
    .object({ roomId: z.string().min(1), userId: z.string().min(1) })
    .strict();
export const RoomMutePayloadSchema = z
    .object({ roomId: z.string().min(1), userId: z.string().min(1) })
    .strict();
export const RoomUnmutePayloadSchema = z
    .object({ roomId: z.string().min(1), userId: z.string().min(1) })
    .strict();
export const ChatSendPayloadSchema = z
    .object({ roomId: z.string().min(1), text: z.string().min(1).max(LIMITS.CHAT_MESSAGE_MAX) })
    .strict();
export const SyncPingPayloadSchema = z.object({ t0: z.number().finite() }).strict();
export const SyncReportPayloadSchema = z
    .object({ roomId: z.string().min(1), position: z.number().finite(), t: z.number().finite() })
    .strict();
// ── Server -> Client payloads ────────────────────────────────────────────
export const RoomStatePayloadSchema = z.object({
    room: RoomSummarySchema,
    members: z.array(MemberSchema),
    role: RoleSchema,
    state: PlaybackStateSchema,
    audioTrackId: z.string().nullable(),
    subtitleTrackId: z.string().nullable(),
    serverTime: z.number().int(),
});
export const RoomMembersPayloadSchema = z.object({ members: z.array(MemberSchema) });
export const RoomHostChangedPayloadSchema = z.object({ hostUserId: z.string() });
export const RoomKickedPayloadSchema = z.object({ reason: z.string() });
export const UserMutedPayloadSchema = z.object({ userId: z.string() });
export const UserUnmutedPayloadSchema = z.object({ userId: z.string() });
export const ChatErrorPayloadSchema = z.object({ message: z.string() });
export const ChatMessagePayloadSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    text: z.string(),
    timestamp: z.number().int(),
});
export const SyncApplyReasonSchema = z.enum(['action', 'heartbeat', 'join']);
export const SyncApplyPayloadSchema = PlaybackStateSchema.extend({
    reason: SyncApplyReasonSchema,
});
export const TrackAudioBroadcastSchema = z.object({ trackId: z.string() });
export const TrackSubtitleBroadcastSchema = z.object({ trackId: z.string().nullable() });
/**
 * A custom (host-uploaded) subtitle track attached to a room.
 * The `playlistUrl` is relative to the HLS base: /hls/<mediaId>/<playlistUrl>.
 * Custom subs are stored under `roomsubs/<roomId>/<id>.vtt` so the existing
 * HLS guard (which checks mediaId + room membership) protects them.
 */
export const RoomSubtitleTrackSchema = z.object({
    id: z.string(),
    label: z.string(),
    language: z.string(),
    playlistUrl: z.string(),
});
export const RoomSubtitleAddedPayloadSchema = RoomSubtitleTrackSchema;
export const SyncPongPayloadSchema = z.object({ t0: z.number(), t1: z.number().int() });
/**
 * Sent to a client whose chat message was rejected due to the rate-limit
 * cooldown. `remainingSeconds` is the whole seconds left until the user can
 * send again (always ≥ 1).
 */
export const ChatCooldownPayloadSchema = z.object({
    remainingSeconds: z.number().int().positive(),
});
export const SocketErrorPayloadSchema = z.object({
    code: z.nativeEnum(ErrorCode),
    message: z.string(),
});
export { LIMITS };
//# sourceMappingURL=socket.js.map